import { BannerElement, BannerSize, DataFeed } from '../types';

export const generateThumbnail = async (
  elements: BannerElement[],
  size: BannerSize,
  dataFeed?: DataFeed | null,
  thumbnailWidth = 300
): Promise<string> => {
  // Calculate thumbnail dimensions maintaining aspect ratio
  const aspectRatio = size.width / size.height;
  const thumbnailHeight = Math.round(thumbnailWidth / aspectRatio);
  const scale = thumbnailWidth / size.width;

  // Create canvas
  const canvas = document.createElement('canvas');
  canvas.width = thumbnailWidth;
  canvas.height = thumbnailHeight;
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Could not get canvas context');
  }

  // Set white background
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, thumbnailWidth, thumbnailHeight);

  // Process content with data feed
  const processContent = (content: string, dataBinding?: string) => {
    if (dataBinding && dataFeed) {
      return String(dataFeed[dataBinding] || content);
    }
    
    if (!dataFeed) return content;
    
    return content.replace(/\{\{(\w+)\}\}/g, (match, field) => {
      return String(dataFeed[field] || match);
    });
  };

  const processImageUrl = (imageUrl: string, dataBinding?: string) => {
    if (dataBinding && dataFeed) {
      return String(dataFeed[dataBinding] || imageUrl);
    }
    
    if (!dataFeed) return imageUrl;
    
    return imageUrl.replace(/\{\{(\w+)\}\}/g, (match, field) => {
      return String(dataFeed[field] || match);
    });
  };

  // Sort elements by z-index
  const sortedElements = [...elements].sort((a, b) => a.zIndex - b.zIndex);

  // Render each element
  for (const element of sortedElements) {
    const x = element.x * scale;
    const y = element.y * scale;
    const width = element.width * scale;
    const height = element.height * scale;

    // Skip if element is completely transparent
    if (element.styles.opacity === 0) continue;

    // Set global alpha for opacity
    ctx.globalAlpha = element.styles.opacity || 1;

    switch (element.type) {
      case 'text':
      case 'button':
        // Set text styles
        const fontSize = (element.styles.fontSize || 16) * scale;
        const fontWeight = element.styles.fontWeight || '400';
        const fontFamily = element.styles.fontFamily || 'Arial, sans-serif';
        
        ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
        ctx.fillStyle = element.styles.color || '#000000';
        ctx.textAlign = element.styles.textAlign || 'center';
        ctx.textBaseline = 'middle';

        // Draw background if specified
        if (element.styles.backgroundColor && element.styles.backgroundColor !== 'transparent') {
          ctx.fillStyle = element.styles.backgroundColor;
          if (element.styles.borderRadius) {
            drawRoundedRect(ctx, x, y, width, height, (element.styles.borderRadius || 0) * scale);
          } else {
            ctx.fillRect(x, y, width, height);
          }
        }

        // Draw text
        ctx.fillStyle = element.styles.color || '#000000';
        const content = processContent(element.content || '', element.dataBinding);
        
        // Calculate text position based on alignment
        let textX = x + width / 2; // center by default
        if (element.styles.textAlign === 'left') {
          textX = x + (element.styles.padding || 0) * scale;
          ctx.textAlign = 'left';
        } else if (element.styles.textAlign === 'right') {
          textX = x + width - (element.styles.padding || 0) * scale;
          ctx.textAlign = 'right';
        }
        
        const textY = y + height / 2;
        
        // Handle text wrapping for long content
        const maxWidth = width - 2 * (element.styles.padding || 0) * scale;
        wrapText(ctx, content, textX, textY, maxWidth, fontSize * 1.2);
        break;

      case 'shape':
        ctx.fillStyle = element.styles.backgroundColor || '#176DB7';
        if (element.styles.borderRadius) {
          drawRoundedRect(ctx, x, y, width, height, (element.styles.borderRadius || 0) * scale);
        } else {
          ctx.fillRect(x, y, width, height);
        }
        break;

      case 'divider':
        ctx.fillStyle = element.styles.backgroundColor || '#000000';
        ctx.fillRect(x, y, width, height);
        break;

      case 'image':
        try {
          const imageUrl = processImageUrl(element.imageUrl || '', element.dataBinding);
          if (imageUrl) {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            
            await new Promise<void>((resolve, reject) => {
              img.onload = () => {
                // Draw image with proper scaling and clipping
                ctx.save();
                
                if (element.styles.borderRadius) {
                  // Create clipping path for rounded corners
                  drawRoundedRect(ctx, x, y, width, height, (element.styles.borderRadius || 0) * scale);
                  ctx.clip();
                }
                
                ctx.drawImage(img, x, y, width, height);
                ctx.restore();
                resolve();
              };
              
              img.onerror = () => {
                // Draw simple gray background for failed images
                ctx.fillStyle = '#f3f4f6';
                ctx.fillRect(x, y, width, height);
                resolve();
              };
              
              img.src = imageUrl;
            });
          } else {
            // Draw simple gray background for missing image URL
            ctx.fillStyle = '#f3f4f6';
            ctx.fillRect(x, y, width, height);
          }
        } catch (error) {
          // Draw simple gray background for errors
          ctx.fillStyle = '#f3f4f6';
          ctx.fillRect(x, y, width, height);
        }
        break;
    }

    // Reset global alpha
    ctx.globalAlpha = 1;
  }

  // Return base64 encoded thumbnail
  return canvas.toDataURL('image/png', 0.8);
};

// Helper function to draw rounded rectangles
const drawRoundedRect = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number
) => {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  ctx.lineTo(x + radius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
  ctx.fill();
};

// Helper function to wrap text
const wrapText = (
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number
) => {
  const words = text.split(' ');
  let line = '';
  let lines: string[] = [];

  for (let n = 0; n < words.length; n++) {
    const testLine = line + words[n] + ' ';
    const metrics = ctx.measureText(testLine);
    const testWidth = metrics.width;
    
    if (testWidth > maxWidth && n > 0) {
      lines.push(line);
      line = words[n] + ' ';
    } else {
      line = testLine;
    }
  }
  lines.push(line);

  // Draw lines centered vertically
  const totalHeight = lines.length * lineHeight;
  const startY = y - totalHeight / 2 + lineHeight / 2;

  lines.forEach((line, index) => {
    ctx.fillText(line.trim(), x, startY + index * lineHeight);
  });
};

export const generateEmptyThumbnail = (size: BannerSize): string => {
  // Smart thumbnail sizing based on aspect ratio
  const aspectRatio = size.width / size.height;
  
  // Define constraints
  const maxDisplayWidth = 280;
  const maxDisplayHeight = 160;
  const minDisplayWidth = 80;
  const minDisplayHeight = 50;
  
  let thumbnailWidth: number;
  let thumbnailHeight: number;
  
  // Calculate optimal dimensions based on aspect ratio
  if (aspectRatio >= 4) {
    // Very wide banners (like 1920×300)
    thumbnailWidth = maxDisplayWidth;
    thumbnailHeight = Math.max(minDisplayHeight, Math.round(thumbnailWidth / aspectRatio));
  } else if (aspectRatio <= 0.25) {
    // Very tall banners (like 300×1200)
    thumbnailHeight = maxDisplayHeight;
    thumbnailWidth = Math.max(minDisplayWidth, Math.round(thumbnailHeight * aspectRatio));
  } else if (aspectRatio > 2) {
    // Wide banners
    thumbnailWidth = Math.min(maxDisplayWidth, size.width * 0.3);
    thumbnailHeight = Math.round(thumbnailWidth / aspectRatio);
  } else if (aspectRatio < 0.5) {
    // Tall banners
    thumbnailHeight = Math.min(maxDisplayHeight, size.height * 0.3);
    thumbnailWidth = Math.round(thumbnailHeight * aspectRatio);
  } else {
    // Normal aspect ratios (0.5 to 2)
    if (size.width > size.height) {
      thumbnailWidth = Math.min(maxDisplayWidth, size.width * 0.4);
      thumbnailHeight = Math.round(thumbnailWidth / aspectRatio);
    } else {
      thumbnailHeight = Math.min(maxDisplayHeight, size.height * 0.4);
      thumbnailWidth = Math.round(thumbnailHeight * aspectRatio);
    }
  }

  // Ensure minimum dimensions
  thumbnailWidth = Math.max(minDisplayWidth, thumbnailWidth);
  thumbnailHeight = Math.max(minDisplayHeight, thumbnailHeight);

  const canvas = document.createElement('canvas');
  canvas.width = thumbnailWidth;
  canvas.height = thumbnailHeight;
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Could not get canvas context');
  }

  // Create clean light gray background
  ctx.fillStyle = '#f8f9fa';
  ctx.fillRect(0, 0, thumbnailWidth, thumbnailHeight);

  // Draw subtle border
  ctx.strokeStyle = '#e9ecef';
  ctx.lineWidth = 1;
  ctx.strokeRect(0.5, 0.5, thumbnailWidth - 1, thumbnailHeight - 1);

  // Calculate responsive sizing for content
  const centerX = thumbnailWidth / 2;
  const centerY = thumbnailHeight / 2;
  
  // Scale content based on thumbnail size
  const scale = Math.min(thumbnailWidth / 200, thumbnailHeight / 120, 1);
  const fontSize = Math.max(10, Math.min(14, 12 * scale));
  const smallFontSize = Math.max(8, Math.min(11, 10 * scale));

  // Draw "Empty" text - very subtle and clean
  ctx.fillStyle = '#adb5bd';
  ctx.font = `400 ${fontSize}px -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('Empty', centerX, centerY - fontSize / 4);

  // Draw dimensions text (only if there's enough space) - even more subtle
  if (thumbnailHeight > 50) {
    ctx.fillStyle = '#ced4da';
    ctx.font = `400 ${smallFontSize}px -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif`;
    
    // Format dimensions for display
    let dimensionText = `${size.width} × ${size.height}`;
    
    // For very wide banners, show on multiple lines if needed
    if (aspectRatio > 5 && thumbnailWidth < 150) {
      ctx.fillText(`${size.width}`, centerX, centerY + fontSize / 2 + smallFontSize);
      ctx.fillText(`× ${size.height}`, centerX, centerY + fontSize / 2 + smallFontSize * 2 + 2);
    } else {
      ctx.fillText(dimensionText, centerX, centerY + fontSize / 2 + smallFontSize + 2);
    }
  }

  return canvas.toDataURL('image/png', 0.9);
};